import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatDate } from "../utils/formatDate";
import {
  Modal,
  Navbar,
  Nav,
  Form,
  FormControl,
  Button,
  Image,
  Container,
  InputGroup,
  Dropdown,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import {
  CiFileOn,
  CiCalendar,
  CiBellOn,
  CiSearch,
  CiSettings,
  CiLogout,
  CiUser,
} from "react-icons/ci";
import { FaDownload, FaFileExcel, FaShieldAlt } from "react-icons/fa";
import "../App.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { LuImport } from "react-icons/lu";
import { BsTags } from "react-icons/bs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import accountTemplate from "../files/Account Template.xlsx";
import mcqTemplate from "../files/MCQ Template.xlsx";
import "../Styles/NavMenu.css";
import AiQuestionGenerator from "./AiQuestionGenerator";
import formatName from "../utils/formatName";

const NavMenu = () => {
  const [activeIcon, setActiveIcon] = useState("");
  const [offcanvasTitle, setOffcanvasTitle] = useState("");
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showIconsModal, setShowIconsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [eventData, setEventData] = useState({
    date: "",
    event_name: "",
    module: "",
  });
  const [tagData, setTagData] = useState({
    tags_name: "",
    description: "",
  });
  const [user, setUser] = useState({
    username: "",
    user_role: "",
    user_id: "",
  });
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    if (loggedInUser) {
      setUser(loggedInUser);
      setUserRole(loggedInUser.user_role);
    }
  }, []);

  const logo = require("../img/TMIS_logo.jpg");
  const profile = require("../img/profile.png");

  const navigate = useNavigate();

  const runSearch = useCallback(
    async (value) => {
      const query = value.trim();

      if (query.length < 2 || !user.user_id) {
        setSearchResults([]);
        setSearchMessage(
          query.length > 0 && query.length < 2
            ? "Enter at least 2 characters to search."
            : "",
        );
        setSearchOpen(query.length > 0);
        return;
      }

      try {
        setSearchLoading(true);
        setSearchMessage("");
        setSearchOpen(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API}/searchContent`,
          { params: { q: query, userId: user.user_id } },
        );

        const results = response.data?.success ? response.data.data || [] : [];
        setSearchResults(results);
        setSearchMessage(
          results.length === 0 ? `No results found for “${query}”.` : "",
        );
      } catch (searchError) {
        console.error("Error searching learning content:", searchError);
        setSearchResults([]);
        setSearchMessage("Search is temporarily unavailable. Please try again.");
      } finally {
        setSearchLoading(false);
      }
    },
    [user.user_id],
  );

  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      setSearchMessage("");
      setSearchOpen(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => runSearch(query), 300);
    return () => window.clearTimeout(timeoutId);
  }, [runSearch, searchQuery]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    runSearch(searchQuery);
  };

  const handleSearchResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    navigate("/assessmentpage", {
      state: {
        moduleId: result.module_id,
        moduleName: result.module_name,
        assessmentId: result.assessment_id || undefined,
      },
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleRegister = () => {
    navigate("/register");
  };

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleOpenIconsModal = () => setShowIconsModal(true);
  const handleCloseIconsModal = () => {
    setShowIconsModal(false);
    window.location.reload();
  };

  const handleOpenImportModal = () => {
    fetchAssessments();
    setShowImportModal(true);
  };
  const handleCloseImportModal = () => setShowImportModal(false);

  const handleDownloadTemplate = (file, fileName) => {
    const link = document.createElement("a");
    link.href = file;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenOffcanvas = (icon, title) => {
    setActiveIcon(icon);
    setOffcanvasTitle(title);
    setShowOffcanvas(true);
  };

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const formattedDate = formatDate(date);
    setEventData({ ...eventData, date: formattedDate });
    setShowEventModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTagData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleInsetInputChange = (e) => {
    const { name, value } = e.target;
    setEventData((prevData) => ({ ...prevData, [name]: value }));
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/getAllAssessment`,
      );
      if (response.data.success) {
        setAssessments(response.data.data);
      } else {
        toast.error("Failed to fetch assessments!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching assessments:", error);
      toast.error(error.response?.data?.message || "Server Error!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tagData.tags_name || !tagData.description) {
      alert("Please fill all fields.");
      return;
    }

    const url = process.env.REACT_APP_API + `/insertTag`;
    try {
      const response = await axios.post(url, tagData);
      if (response.data.success) {
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 3000,
        });
        setTagData({
          tags_name: "",
          description: "",
        });
      } else {
        toast.error("Failed to save tag!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving tag:", error);
      toast.error(error.response?.data?.message || "Server Error!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleAssessmentChange = (e) => {
    setSelectedAssessment(e.target.value);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedAssessment) {
      toast.error("Please select an assessment!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("assessment_id", selectedAssessment);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/excelImport`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 3000,
        });
        setSelectedFile(null);
        setSelectedAssessment("");
        handleCloseImportModal();
      } else {
        toast.error("Failed to import file.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.message || "Server Error!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInsertEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    const [day, month, year] = eventData.date.split("-");
    const formattedDate = `${year}-${month}-${day}`;

    const eventToSend = { ...eventData, date: formattedDate };
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/insertEvent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventToSend),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Event saved successfully!", {
          position: "top-right",
          autoClose: 3000,
        });

        setEventData({ date: "", event_name: "", module: "" });
        handleCloseModal();
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        toast.error(data.message || "Failed to save event.", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API}/getEvent`);
        const data = await response.json();

        if (data.success) {
          setEvents(data.events);
        } else {
          setError(data.message || "Failed to fetch events.");
        }
      } catch (err) {
        setError("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const fetchNotification = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getNotification`,
        );
        const data = await response.json();

        if (data.success) {
          setNotifications(data.noti);
        } else {
          setError(data.message || "Failed to fetch notification.");
        }
      } catch (err) {
        setError("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
    fetchNotification();
  }, []);

  const eventDates = events.map((event) => event.date);

  const isEventDate = (date) => {
    const formattedDate =
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0");
    return eventDates.includes(formattedDate);
  };

  return (
    <>
      <Navbar bg="light" expand="lg" fixed="top" className="shadow navbar p-0">
        <Container>
          <Navbar.Brand href="/">
            <Image
              src={logo}
              width="120"
              alt="Logo"
              className="d-inline-block align-top"
            />
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="navbar-nav" />

          <Navbar.Collapse id="navbar-nav">
            <Nav className="mx-auto" style={{ flex: 1 }}>
              <Form
                className="mx-auto w-75 nav-global-search"
                onSubmit={handleSearchSubmit}
                ref={searchContainerRef}
                role="search"
              >
                <InputGroup className="w-100 nav-global-search__group">
                  <InputGroup.Text
                    id="search-icon"
                    className="nav-global-search__icon"
                  >
                    <CiSearch size={23} aria-hidden="true" />
                  </InputGroup.Text>
                  <FormControl
                    id="global-content-search"
                    type="text"
                    placeholder="Search modules and assessments"
                    aria-label="Search modules and assessments"
                    aria-describedby="search-icon"
                    aria-expanded={searchOpen}
                    aria-controls="global-search-results"
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onFocus={() => {
                      if (searchQuery.trim()) setSearchOpen(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setSearchOpen(false);
                    }}
                  />
                  <Button
                    type="submit"
                    className="button-color"
                    disabled={searchLoading}
                  >
                    {searchLoading ? "Searching…" : "Search"}
                  </Button>
                </InputGroup>

                {searchOpen && (
                  <div
                    className="nav-global-search__results"
                    id="global-search-results"
                    role="listbox"
                    aria-label="Search results"
                  >
                    {searchLoading && searchResults.length === 0 ? (
                      <div className="nav-global-search__status" role="status">
                        <Spinner animation="border" size="sm" />
                        Searching learning content…
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((result) => (
                        <button
                          type="button"
                          role="option"
                          aria-selected="false"
                          className="nav-global-search__result"
                          key={`${result.result_type}-${
                            result.assessment_id || result.module_id
                          }`}
                          onClick={() => handleSearchResultClick(result)}
                        >
                          <span className="nav-global-search__result-badge">
                            {result.result_type === "assessment"
                              ? result.assessment_type || "Assessment"
                              : "Module"}
                          </span>
                          <span className="nav-global-search__result-copy">
                            <strong>{result.title}</strong>
                            <small>
                              {result.result_type === "assessment"
                                ? `In ${result.module_name}`
                                : result.subtitle || "Learning module"}
                            </small>
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="nav-global-search__status">
                        {searchMessage || "Start typing to search."}
                      </div>
                    )}
                  </div>
                )}
              </Form>
            </Nav>

            <Nav className="ms-auto align-items-center gap-2">
              <Nav.Link
                href="#"
                className="nav-icon-color m-0"
                onClick={() =>
                  handleOpenOffcanvas("file", "Assessment to be reviewed")
                }
                style={{
                  color: activeIcon === "file" ? "#263F86" : "inherit",
                }}
              >
                <CiFileOn size={30} />
              </Nav.Link>
              {userRole === "admin" && (
                <Nav.Link
                  href="#"
                  className="nav-icon-color m-0"
                  onClick={() => handleOpenOffcanvas("calendar", "Calendar")}
                  style={{
                    color: activeIcon === "calendar" ? "#263F86" : "inherit",
                  }}
                >
                  <CiCalendar size={30} />
                </Nav.Link>
              )}
              <Nav.Link
                href="#"
                className="nav-icon-color m-0"
                onClick={() => handleOpenOffcanvas("bell", "Notifications")}
                style={{
                  color: activeIcon === "bell" ? "#263F86" : "inherit",
                }}
              >
                <CiBellOn size={30} />
              </Nav.Link>
              <div style={{ fontSize: "14px", fontFamily: "Manrope-Regular" }}>
                <strong>{formatName(user.username)}</strong> ({user.user_role})
              </div>

              <Dropdown align="end" className="ms-3 nav-icon-color">
                <Dropdown.Toggle
                  variant="link"
                  id="profile-dropdown"
                  className="p-0 d-flex align-items-center"
                >
                  <Image src={profile} width="40" alt="Profile" roundedCircle />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {user.user_role === "admin" && (
                    <>
                      <Dropdown.Item onClick={handleRegister}>
                        <CiUser size={20} className="me-2" />
                        Register
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleOpenIconsModal}>
                        <CiSettings size={20} className="me-2" />
                        Settings
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Item onClick={handleLogout}>
                    <CiLogout size={20} className="me-2" />
                    Log Out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Modal
        show={showIconsModal}
        onHide={handleCloseIconsModal}
        size="lg"
        centered
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: "1px solid #EEF0F4",
            padding: "20px 24px",
          }}
        >
          <Modal.Title
            style={{
              color: "#263F86",
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            Settings
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div onClick={handleOpenImportModal} style={settingCardStyle}>
                <div style={settingIconStyle}>
                  <LuImport size={22} />
                </div>

                <h6 style={settingTitleStyle}>Import MCQ</h6>
                <p style={settingTextStyle}>
                  Upload questions into selected assessment.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div onClick={handleOpenModal} style={settingCardStyle}>
                <div style={settingIconStyle}>
                  <BsTags size={22} />
                </div>

                <h6 style={settingTitleStyle}>Tags</h6>
                <p style={settingTextStyle}>
                  Create and manage assessment tags.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div
                onClick={() => setShowAiModal(true)}
                style={settingCardStyle}
              >
                <div style={settingIconStyle}>
                  <FaShieldAlt size={22} />
                </div>

                <h6 style={settingTitleStyle}>AI & Data Protection</h6>
                <p style={settingTextStyle}>
                  OpenAI question generation with PDPA safeguards.
                </p>
              </div>
            </div>
          </div>

          <div
            className="mt-4"
            style={{
              background: "#F8FAFC",
              border: "1px solid #EEF0F4",
              borderRadius: "18px",
              padding: "18px",
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaDownload color="#263F86" size={15} />
              <h6
                className="fw-bold mb-0"
                style={{
                  color: "#1F2937",
                  fontSize: "15px",
                }}
              >
                Download Templates
              </h6>
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() =>
                    handleDownloadTemplate(
                      accountTemplate,
                      "Account Template.xlsx",
                    )
                  }
                  style={templateButtonStyle}
                >
                  <div style={templateIconStyle}>
                    <FaFileExcel size={18} />
                  </div>

                  <div style={{ textAlign: "left" }}>
                    <div style={templateTitleStyle}>Account Template</div>
                    <div style={templateTextStyle}>
                      Excel file for learner account import
                    </div>
                  </div>
                </button>
              </div>

              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() =>
                    handleDownloadTemplate(mcqTemplate, "MCQ Template.xlsx")
                  }
                  style={templateButtonStyle}
                >
                  <div style={templateIconStyle}>
                    <FaFileExcel size={18} />
                  </div>

                  <div style={{ textAlign: "left" }}>
                    <div style={templateTitleStyle}>MCQ Template</div>
                    <div style={templateTextStyle}>
                      Excel file for MCQ assessment import
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <AiQuestionGenerator
        show={showAiModal}
        onHide={() => setShowAiModal(false)}
        user={user}
      />
      <Modal
        show={showImportModal}
        onHide={handleCloseImportModal}
        size="lg"
        centered
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: "1px solid #EEF0F4",
            padding: "20px 24px",
          }}
        >
          <Modal.Title
            style={{
              color: "#263F86",
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            Import Excel File
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #EEF0F4",
              borderRadius: "16px",
              padding: "18px",
              marginBottom: "20px",
            }}
          >
            <h6
              className="fw-bold mb-1"
              style={{
                color: "#1F2937",
                fontSize: "15px",
              }}
            >
              Upload MCQ Excel
            </h6>

            <p
              className="mb-0"
              style={{
                color: "#64748B",
                fontSize: "13px",
              }}
            >
              Select an assessment and upload your MCQ Excel template file.
            </p>
          </div>

          <Form>
            <Form.Group controlId="formAssessmentSelect" className="mb-3">
              <Form.Label className="fw-semibold">Select Assessment</Form.Label>

              <Form.Control
                as="select"
                value={selectedAssessment}
                onChange={handleAssessmentChange}
                style={{
                  borderRadius: "12px",
                  padding: "11px 14px",
                  border: "1px solid #EEF0F4",
                  fontSize: "14px",
                }}
              >
                <option value="">-- Select an Assessment --</option>
                {assessments.map((assessment) => (
                  <option
                    key={assessment.assessment_id}
                    value={assessment.assessment_id}
                  >
                    {assessment.assessment_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formFile" className="mb-0">
              <Form.Label className="fw-semibold">Upload Excel File</Form.Label>

              <Form.Control
                type="file"
                accept=".xlsx, .xls"
                onChange={(event) => setSelectedFile(event.target.files[0])}
                style={{
                  borderRadius: "12px",
                  padding: "11px 14px",
                  border: "1px solid #EEF0F4",
                  fontSize: "14px",
                }}
              />

              <small
                style={{
                  color: "#64748B",
                  display: "block",
                  marginTop: "8px",
                }}
              >
                Supported formats: .xlsx and .xls
              </small>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "1px solid #EEF0F4",
            padding: "18px 24px",
          }}
        >
          <Button
            variant="light"
            onClick={handleCloseImportModal}
            disabled={loading}
            style={{
              borderRadius: "10px",
              padding: "9px 16px",
              fontWeight: 700,
              border: "1px solid #EEF0F4",
            }}
          >
            Close
          </Button>

          <Button
            onClick={handleFileUpload}
            disabled={loading}
            style={{
              background: "#263F86",
              color: "#ffffff",
              border: "none",
              borderRadius: "10px",
              padding: "9px 18px",
              fontWeight: 700,
            }}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Importing...
              </>
            ) : (
              "Import File"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {user.user_role === "admin" && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="lg"
          centered
          style={{ fontFamily: "Manrope-Regular" }}
        >
          <form onSubmit={handleSubmit}>
            <Modal.Header
              closeButton
              style={{
                borderBottom: "1px solid #EEF0F4",
                padding: "20px 24px",
              }}
            >
              <Modal.Title
                style={{
                  color: "#263F86",
                  fontWeight: 800,
                  fontSize: "20px",
                }}
              >
                Create Tags
              </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: "24px" }}>
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #EEF0F4",
                  borderRadius: "16px",
                  padding: "18px",
                  marginBottom: "20px",
                }}
              >
                <h6
                  className="fw-bold mb-1"
                  style={{
                    color: "#1F2937",
                    fontSize: "15px",
                  }}
                >
                  Assessment Tags
                </h6>

                <p
                  className="mb-0"
                  style={{
                    color: "#64748B",
                    fontSize: "13px",
                  }}
                >
                  Create reusable tags for organizing assessments and questions.
                </p>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Tag Name</Form.Label>

                <Form.Control
                  type="text"
                  placeholder="Enter tag name"
                  name="tags_name"
                  value={tagData.tags_name}
                  onChange={handleInputChange}
                  style={{
                    borderRadius: "12px",
                    padding: "11px 14px",
                    border: "1px solid #EEF0F4",
                    fontSize: "14px",
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label className="fw-semibold">Description</Form.Label>

                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Write short description"
                  name="description"
                  value={tagData.description}
                  onChange={handleInputChange}
                  style={{
                    borderRadius: "12px",
                    padding: "12px 14px",
                    border: "1px solid #EEF0F4",
                    resize: "none",
                    fontSize: "14px",
                  }}
                />

                <small
                  style={{
                    color: "#64748B",
                    display: "block",
                    marginTop: "8px",
                  }}
                >
                  Example: City, Green, Heritage, Chinese, Indian
                </small>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer
              style={{
                borderTop: "1px solid #EEF0F4",
                padding: "18px 24px",
              }}
            >
              <Button
                variant="light"
                onClick={handleCloseModal}
                style={{
                  borderRadius: "10px",
                  padding: "9px 16px",
                  fontWeight: 700,
                  border: "1px solid #EEF0F4",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                style={{
                  background: "#263F86",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "9px 18px",
                  fontWeight: 700,
                }}
              >
                Save Tag
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      )}

      <Offcanvas
        show={showOffcanvas}
        onHide={handleCloseOffcanvas}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{offcanvasTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ fontFamily: "Manrope-Regular" }}>
          {activeIcon === "calendar" && (
            <div>
              <p className="fw-semibold">Schedule for Assessments</p>
              <Calendar
                onChange={handleDateClick}
                value={selectedDate}
                tileContent={({ date }) =>
                  isEventDate(date) ? (
                    <div className="event-circle"></div> // Show circle if event exists
                  ) : null
                }
              />
              {/* <Calendar onChange={handleDateClick} value={selectedDate} /> */}

              <p className="mt-3 fw-semibold">Upcoming Events</p>

              {loading && <p>Loading events...</p>}

              {error && <p className="text-danger">{error}</p>}

              {events.length > 0 ? (
                events.map((event, index) => {
                  // Extract date parts
                  const [year, month, day] = event.date.split("-");
                  const eventDate = new Date(year, month - 1, day);
                  const monthName = eventDate.toLocaleString("default", {
                    month: "short",
                  });

                  return (
                    <div
                      key={index}
                      className={`event-card p-3 mb-3 ${
                        event.status === "cancelled" ? "cancelled" : ""
                      }`}
                    >
                      <div className="d-flex">
                        {/* Left Side - Date */}
                        <div className="date-box text-center">
                          <span className="day">{day}</span>
                          <span className="month">
                            {monthName.toUpperCase()}
                          </span>
                        </div>

                        {/* Right Side - Event Details */}
                        <div className="event-details ms-3">
                          <h5 className="event-title">{event.event_name}</h5>
                          <p className="event-location">{event.module}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted">No upcoming events.</p>
              )}
            </div>
          )}
          {activeIcon === "bell" && (
            <div>
              {notifications.length > 0 ? (
                notifications.map((noti, index) => (
                  <div key={index} className="notification-card">
                    <div className="notification-content">
                      <div>
                        <strong>{noti.noti_name}</strong> ·{" "}
                        <span className="text-muted">
                          {formatDate(noti.created_at)}{" "}
                        </span>
                        <p className="notification-description mt-2">
                          {noti.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted">No new notifications.</p>
              )}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showEventModal}
        onHide={() => setShowEventModal(false)}
        centered
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#263F86" }}>
            Create Schedule for Assessment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleInsertEvent}>
            <Form.Group controlId="formDate">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="text"
                name="date"
                value={eventData.date}
                readOnly
              />
            </Form.Group>
            <Form.Group controlId="formEvent" className="mt-3">
              <Form.Label>Event Description</Form.Label>
              <Form.Control
                type="text"
                name="event_name"
                value={eventData.event_name}
                onChange={handleInsetInputChange}
              />
            </Form.Group>
            <Form.Group controlId="formModule" className="mt-3">
              <Form.Label>Module</Form.Label>
              <Form.Control
                type="text"
                name="module"
                value={eventData.module}
                onChange={handleInsetInputChange}
              />
            </Form.Group>
            <Modal.Footer>
              <Button type="submit" style={{ background: "#263F86" }}>
                Save Event
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </>
  );
};

const settingCardStyle = {
  background: "#ffffff",
  border: "1px solid #EEF0F4",
  borderRadius: "18px",
  padding: "18px",
  minHeight: "150px",
  cursor: "pointer",
  boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
};

const settingIconStyle = {
  width: "44px",
  height: "44px",
  borderRadius: "14px",
  background: "rgba(95, 112, 245, 0.15)",
  color: "#263F86",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "14px",
};

const settingTitleStyle = {
  fontWeight: 800,
  color: "#1F2937",
  marginBottom: "6px",
};

const settingTextStyle = {
  fontSize: "13px",
  color: "#64748B",
  marginBottom: 0,
  lineHeight: 1.5,
};

const templateButtonStyle = {
  width: "100%",
  border: "1px solid #E2E8F0",
  background: "#ffffff",
  borderRadius: "14px",
  padding: "14px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
};

const templateIconStyle = {
  width: "40px",
  height: "40px",
  borderRadius: "12px",
  background: "#ECFDF5",
  color: "#15803D",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const templateTitleStyle = {
  fontSize: "14px",
  fontWeight: 800,
  color: "#1F2937",
};

const templateTextStyle = {
  fontSize: "12px",
  color: "#64748B",
};

export default NavMenu;
